import { Link } from "react-router-dom";
import { Leaf, ShieldCheck, Palette, Truck, CheckCircle2, ArrowRight, Recycle, BadgeCheck, Package } from "lucide-react";
import { PACKAGING, PACKAGING_CERTIFICATIONS } from "@/lib/company";
import SampleKitForm from "@/components/SampleKitForm";

export default function EcoPackaging() {
  return (
    <div data-testid="eco-packaging-page">
      {/* Hero */}
      <section className="relative min-h-[62vh] flex items-center overflow-hidden bg-gradient-to-br from-brand-green-dark to-brand-green">
        <div className="absolute inset-0 opacity-40">
          <img src={PACKAGING.heroImage} alt="Compostable food-service packaging" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(18,64,21,0.85) 0%, rgba(27,94,32,0.72) 60%, rgba(18,64,21,0.95) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-24 w-full">
          <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-5">Category E</span>
          <h1 className="font-display font-bold text-white text-3xl md:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Engineered for Excellence.<br />
            <span className="text-brand-gold">Designed for the Planet.</span>
          </h1>
          <p className="mt-6 text-white/85 text-lg max-w-3xl">
            {PACKAGING.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="packaging-hero-cta" className="btn-gold">Request a Consultation</Link>
            <Link to="/contact" data-testid="packaging-hero-specs" className="btn-outline-white">Download Specifications</Link>
            <Link
              to="/products/eco-packaging/experience"
              data-testid="packaging-hero-experience"
              className="inline-flex items-center gap-2 px-6 py-3 text-[11px] tracking-[0.35em] uppercase text-white/90 border border-brand-gold/50 rounded-sm hover:border-brand-gold hover:bg-brand-gold/10 transition"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
              Enter the Experience
            </Link>
          </div>
        </div>
      </section>

      {/* Category cards */}
      <section className="py-20 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">The Collection</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl gold-underline-center">
              Six Precision-Engineered Categories
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PACKAGING.categories.map((cat) => (
              <a
                key={cat.slug}
                href={`#${cat.slug}`}
                data-testid={`packaging-nav-${cat.slug}`}
                className="product-card group bg-white rounded-lg border border-neutral-200 overflow-hidden flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden bg-white flex items-center justify-center p-4">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display font-bold text-brand-charcoal text-lg">{cat.name}</h3>
                  <p className="mt-1 text-xs italic text-brand-gold">{cat.tagline}</p>
                  <div className="mt-auto pt-4 text-brand-green text-sm font-semibold inline-flex items-center gap-1.5 group-hover:text-brand-gold transition-colors">
                    View Specifications <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed sections */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 space-y-14">
          {PACKAGING.categories.map((cat, idx) => (
            <div
              key={cat.slug}
              id={cat.slug}
              data-testid={`packaging-detail-${cat.slug}`}
              className={`bg-brand-cream rounded-lg border border-neutral-200 overflow-hidden grid lg:grid-cols-2 gap-0 scroll-mt-24 ${idx % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
            >
              <div className="aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden bg-white flex items-center justify-center p-6">
                <img src={cat.image} alt={cat.name} className="w-full h-full object-contain" />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Category {String.fromCharCode(65 + idx)}</span>
                <div className="font-accent italic text-brand-gold text-base mt-2">{cat.tagline}</div>
                <h2 className="mt-1 font-display font-bold text-brand-charcoal text-3xl">{cat.name}</h2>
                <p className="mt-4 text-brand-charcoal/80 leading-relaxed text-sm">{cat.description}</p>

                <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-bold mb-2">Sizes / Formats</div>
                    <ul className="space-y-1">
                      {cat.sizes.map((s) => (
                        <li key={s} className="flex gap-2 items-start text-brand-charcoal/85">
                          <CheckCircle2 className="w-3.5 h-3.5 text-brand-green shrink-0 mt-0.5" />{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-bold mb-2">Finishes</div>
                    <ul className="space-y-1">
                      {cat.finishes.map((f) => (
                        <li key={f} className="flex gap-2 items-start text-brand-charcoal/85">
                          <CheckCircle2 className="w-3.5 h-3.5 text-brand-green shrink-0 mt-0.5" />{f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="text-xs uppercase tracking-widest text-brand-gold font-bold">Ideal Use Case</div>
                  <div className="mt-1 text-brand-charcoal/85 text-sm">{cat.useCase}</div>
                </div>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/contact" data-testid={`packaging-spec-${cat.slug}`} className="btn-green" style={{ padding: "0.7rem 1.2rem", fontSize: "0.9rem" }}>
                    Request Spec Sheet
                  </Link>
                  <Link to="/contact" data-testid={`packaging-sample-${cat.slug}`} className="btn-outline-green" style={{ padding: "0.7rem 1.2rem", fontSize: "0.9rem" }}>
                    Order Sample
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Sustainability */}
      <section className="py-24 bg-brand-green text-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Sustainability & Compliance</span>
          <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl lg:text-5xl gold-underline-center">
            Performance Without Compromise
          </h2>
          <p className="mt-6 text-white/85 leading-relaxed text-lg max-w-3xl mx-auto">
            Every product in our portfolio is certified compostable, manufactured to the highest food-grade standards, and designed to decompose responsibly. We provide the global hospitality industry with packaging that performs flawlessly today and leaves no trace tomorrow.
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Recycle, label: "Certified Compostable" },
              { icon: ShieldCheck, label: "Food-Safe Grade" },
              { icon: Palette, label: "Custom Branding Ready" },
              { icon: Truck, label: "Global Logistics Capable" },
            ].map((f, i) => (
              <div key={i} data-testid={`packaging-feature-${i}`} className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-md bg-brand-gold/20 flex items-center justify-center border border-brand-gold/40">
                  <f.icon className="w-6 h-6 text-brand-gold" />
                </div>
                <div className="text-sm font-semibold uppercase tracking-widest text-brand-gold">{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customization */}
      <section className="py-24 bg-brand-cream">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Customization Studio</span>
              <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline">
                Your Brand.<br /><span className="text-brand-green">Our Canvas.</span>
              </h2>
              <p className="mt-6 text-brand-charcoal/80 leading-relaxed">
                From precision colour matching to proprietary embossing and full-wrap printing, we transform standard packaging into bespoke brand assets. Our technical team manages plate development, colour calibration, and production oversight to ensure your packaging arrives exactly as specified.
              </p>
              <ul className="mt-6 space-y-3 text-brand-charcoal/85">
                {[
                  "Precision Pantone colour matching",
                  "Custom embossing plate development",
                  "Full-wrap printing on all cup formats",
                  "Custom printed paper straws (4-ply)",
                  "Pre-press proofing and press-side approval",
                ].map((it) => (
                  <li key={it} className="flex gap-3 items-start">
                    <Leaf className="w-4 h-4 text-brand-green mt-1 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-brand-charcoal/60 italic">
                Custom printing and embossing services available. One-time plate investments required for proprietary designs.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {PACKAGING.customExamples.map((src, i) => (
                <div key={i} className="aspect-square rounded-lg overflow-hidden shadow-md bg-white">
                  <img src={src} alt="Custom brand example" className="w-full h-full object-contain" />
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 text-xs text-center text-brand-charcoal/50 italic">
            Examples of custom printing capabilities. Client logos shown are for illustrative reference only.
          </div>
        </div>
      </section>

      {/* Certifications badge strip */}
      <section className="py-20 bg-white border-t border-neutral-200" data-testid="packaging-certs-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Independently Certified</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline-center">
              Every Claim, Verified by Accredited Bodies
            </h2>
            <p className="mt-5 text-brand-charcoal/75 leading-relaxed text-sm md:text-base">
              From forest fibre to composting stream, our packaging is audited under 17 international standards covering compostability, food-contact safety, forest-of-origin traceability, and ethical manufacturing.
            </p>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {PACKAGING_CERTIFICATIONS.flatMap((g) => g.items).map((it) => (
              <div
                key={it.full}
                data-testid={`packaging-badge-${it.short.toLowerCase().replace(/\s+/g, "-")}`}
                title={it.full + " — " + it.detail}
                className="aspect-square rounded-md border border-neutral-200 bg-[#F7F5EF] flex flex-col items-center justify-center text-center px-2 hover:border-brand-gold hover:bg-white transition group"
              >
                <BadgeCheck className="w-5 h-5 text-brand-green mb-1.5 group-hover:text-brand-gold transition-colors" />
                <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-wider text-brand-charcoal leading-tight">
                  {it.short}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 justify-center text-xs text-brand-charcoal/60">
            <span>CPCB · India</span>
            <span>EN 13432 · EU</span>
            <span>ASTM D6400 · US</span>
            <span>AS 5810 · AU</span>
            <span>FSC · Global</span>
            <span>BRCGS · Global</span>
            <span>SEDEX SMETA · Ethical</span>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/certifications#packaging-certs-section"
              data-testid="packaging-certs-view-all"
              className="btn-outline-green inline-flex"
            >
              View Full Certification Register
            </Link>
          </div>
        </div>
      </section>

      {/* Sample Kit CTA */}
      <section className="py-24 bg-brand-cream" data-testid="sample-kit-section" id="sample-kit">
        <div className="max-w-7xl mx-auto px-4">
          {/* Country flag row — top buyer destinations */}
          <div className="mb-14 text-center" data-testid="sample-kit-flag-row">
            <div className="text-[11px] font-bold uppercase tracking-[0.35em] text-brand-gold mb-5">
              Trusted by Buyers in
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
              {[
                { code: "gb", label: "United Kingdom", short: "UK" },
                { code: "ae", label: "United Arab Emirates", short: "UAE" },
                { code: "us", label: "United States", short: "US" },
                { code: "au", label: "Australia", short: "Australia" },
                { code: "de", label: "Germany", short: "Germany" },
                { code: "sg", label: "Singapore", short: "Singapore" },
              ].map((c) => (
                <div
                  key={c.code}
                  data-testid={`flag-${c.code}`}
                  className="flex flex-col items-center gap-2 group"
                  title={c.label}
                >
                  <div className="w-14 h-10 rounded-sm overflow-hidden shadow-md border border-neutral-300 group-hover:border-brand-gold transition">
                    <img
                      src={`https://flagcdn.com/w80/${c.code}.png`}
                      srcSet={`https://flagcdn.com/w160/${c.code}.png 2x`}
                      alt={`${c.label} flag`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="text-xs font-semibold text-brand-charcoal/75 group-hover:text-brand-charcoal transition">
                    {c.short}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-xs text-brand-charcoal/55 max-w-2xl mx-auto">
              Sample kits shipped to hospitality groups, distributors and QSR chains across 20+ countries.
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2 space-y-6">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Feel Before You Order</span>
              <h2 className="font-display font-bold text-brand-charcoal text-3xl md:text-4xl lg:text-5xl leading-tight gold-underline">
                Request a Free<br />
                <span className="text-brand-green">Physical Sample Kit</span>
              </h2>
              <p className="text-brand-charcoal/80 leading-relaxed">
                Paper weight, ripple depth, embossing crispness, lid seal — these can't be judged from a screen. We ship a curated sample kit anywhere in the world so your team can touch, pour, and pressure-test before committing to a container.
              </p>
              <ul className="space-y-3 pt-2">
                {[
                  "One cup from each requested category",
                  "Kraft, white, printed & embossed finishes side-by-side",
                  "Matching lids & 4-ply paper straws",
                  "Certificate summary sheet enclosed",
                ].map((it) => (
                  <li key={it} className="flex gap-3 items-start text-sm text-brand-charcoal">
                    <Package className="w-4 h-4 text-brand-green mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-4 flex items-center gap-4 text-xs text-brand-charcoal/60">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-brand-gold" />
                  <span>Dispatched within 5 business days</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3">
              <SampleKitForm />
            </div>
          </div>
        </div>
      </section>

      {/* Wholesale CTA */}
      <section className="py-24 bg-[#212121] text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight">
            Ready to Elevate<br /><span className="text-brand-gold">Your Packaging?</span>
          </h2>
          <p className="mt-6 text-white/75 text-lg">
            We partner with distributors, hospitality groups, and F&B chains worldwide. Connect with our sourcing team to discuss volume requirements, custom specifications, and global logistics.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="packaging-cta-consultation" className="btn-gold">Request a Consultation</Link>
            <Link to="/contact" data-testid="packaging-cta-specs" className="btn-outline-white">Download Specifications</Link>
          </div>
          <p className="mt-10 text-xs text-white/50 italic max-w-2xl mx-auto">
            All products are packed in export-standard cartons optimised for 40' High Cube container loading. Minimum order quantities apply for international shipments. Custom design services include pre-press proofing and colour matching.
          </p>
        </div>
      </section>
    </div>
  );
}
